import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FaArrowRight, FaInfoCircle } from 'react-icons/fa';

import {
  TechSection,
  TechSelect,
  TechButtonGhost,
  StatusPill,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../../styles/techComponents';

import { TEMPLATES, TYPE_OPTIONS } from '../templates';

const TopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0.85rem;
`;

const Card = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${accent};
  border-radius: 6px;
  padding: 0.95rem 1.05rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px; height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Title = styled.h4`
  margin: 0;
  color: var(--text-primary);
  font-size: 0.98rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);

  span {
    background: ${panelDeep};
    border: 1px solid ${borderSoft};
    border-radius: 3px;
    padding: 1px 6px;
  }
  .type { color: ${accent}; border-color: rgba(0, 180, 216, 0.3); }
`;

const Body = styled.div`
  font-size: 0.86rem;
  line-height: 1.5;
  color: var(--text-secondary);

  .topic {
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }
  .details {
    color: var(--text-disabled);
    word-break: break-word;
  }
`;

const Hint = styled.p`
  margin: 0 0 1rem;
  padding: 0.75rem 1rem;
  background: rgba(0, 180, 216, 0.05);
  border: 1px solid rgba(0, 180, 216, 0.2);
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 0.86rem;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;

  svg { color: ${accent}; flex-shrink: 0; margin-top: 3px; }
`;

const TemplatesTab = ({ onUseTemplate }) => {
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(
    () => (filter === 'all' ? TEMPLATES : TEMPLATES.filter((t) => t.type === filter)),
    [filter]
  );

  const totalsByType = useMemo(() => {
    const t = {};
    for (const tpl of TEMPLATES) t[tpl.type] = (t[tpl.type] || 0) + 1;
    return t;
  }, []);

  return (
    <>
      <Hint>
        <FaInfoCircle />
        Las plantillas son <strong>disparadores de partida</strong>: pre-llenan el formulario del Copilot
        con un tema y unas instrucciones tipo. La IA después usa el contexto en vivo de la plataforma
        (competidores, noticias scrapeadas, insights, objetivos) para producir el contenido final.
        Para usarlas, cliqueá una en el tab <strong>Copilot</strong> donde aparecen como "Plantillas de partida".
      </Hint>

      <TopBar>
        <TechSelect value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Todos los tipos · {TEMPLATES.length}</option>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}{totalsByType[o.value] ? ` · ${totalsByType[o.value]}` : ''}
            </option>
          ))}
        </TechSelect>
        <StatusPill>Plantillas <strong>{filtered.length}</strong></StatusPill>
      </TopBar>

      <TechSection title="Catálogo de plantillas">
        <Cards>
          {filtered.map((tpl) => (
            <Card key={tpl.id}>
              <Title>
                <span style={{ fontSize: '1.1rem' }}>{tpl.icon}</span>
                {tpl.title}
              </Title>
              <Meta>
                <span className="type">{tpl.type}</span>
                {tpl.channel && <span>{tpl.channel}</span>}
                <span>{tpl.audience}</span>
                <span>{tpl.tone}</span>
              </Meta>
              <Body>
                <div className="topic"><strong>Tema:</strong> {tpl.topic}</div>
                <div className="details">{tpl.details}</div>
              </Body>
              {onUseTemplate && (
                <TechButtonGhost type="button" onClick={() => onUseTemplate(tpl)}>
                  Usar en Copilot <FaArrowRight />
                </TechButtonGhost>
              )}
            </Card>
          ))}
        </Cards>
      </TechSection>
    </>
  );
};

export default TemplatesTab;
